/**
 * AML Service
 *
 * Service for managing AML (Anti-Money Laundering) issues and two-person review workflow.
 * Follows compliance.service.ts patterns with AbortSignal support for lifecycle management.
 *
 * Based on AML_UI_UX_DESIGN.md v2.2 specification.
 */

import { api } from '@/lib/api';
import { toast } from 'sonner';
import type {
  AMLIssue,
  AMLIssueStatus,
  AMLIssueType,
  AMLIssuesQueryParams,
  AMLIssuesResponse,
  AMLSummaryResponse,
  AMLCountsResponse,
  AMLMonthCountsResponse,
  ClaimIssueRequest,
  ClaimIssueResponse,
  ReleaseClaimRequest,
  FirstReviewRequest,
  FirstReviewResponse,
  SecondReviewRequest,
  SecondReviewResponse,
  AddNoteRequest,
  AddNoteResponse,
  AddInfoRequest,
  AddInfoResponse,
  BulkClaimRequest,
  BulkClaimResponse,
  BulkFirstReviewRequest,
  BulkFirstReviewResponse,
  BulkSecondReviewRequest,
  BulkSecondReviewResponse,
  SnoozeIssueRequest,
  BulkSnoozeRequest,
  UnsnoozeIssueRequest,
  BulkUnsnoozeRequest,
  ReassignIssueRequest,
  OverrideSeparationRequest,
  ProposedOutcome,
  SecondReviewDecision,
} from '@/types/aml.types';
import {
  AML_ISSUE_TYPE_INFO,
  AML_STATUS_BADGE_CONFIG,
} from '@/types/aml.types';

/**
 * Utility function to check if an error is a request cancellation
 * Handles both native AbortError and Axios CanceledError
 */
export function isAMLRequestCancelled(error: unknown): error is Error {
  return (
    error instanceof Error &&
    (error.name === 'AbortError' ||
     error.name === 'CanceledError' ||
     (error as unknown as { code?: string }).code === 'ERR_CANCELED')
  );
}

/**
 * Validate issue hash format (32 characters MD5 or 64 characters SHA-256)
 * AML issues use MD5 hashes (32 chars), compliance issues use SHA-256 (64 chars)
 */
function isValidIssueHash(hash: string): boolean {
  return typeof hash === 'string' && /^[a-f0-9]{32,64}$/i.test(hash);
}

/**
 * Validate entity ID format (UUID)
 */
function isValidEntityId(entityId: string): boolean {
  return (
    typeof entityId === 'string' &&
    entityId.trim().length > 0 &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(entityId)
  );
}

class AMLService {
  private readonly baseUrl = '/aml';

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  /**
   * Create a status change comment for audit trail (like Compliance pattern)
   * Comments are posted to the unified Comments system with [SYSTEM] prefix
   */
  private async createStatusChangeComment(
    issueHash: string,
    action: 'first_review' | 'second_review_agree' | 'second_review_needs_info' | 'add_info' | 'claim' | 'release',
    reason: string,
    metadata?: {
      proposedOutcome?: ProposedOutcome;
      issueType?: string;
      issueTitle?: string;
    }
  ): Promise<void> {
    try {
      // Import CommentsService dynamically to avoid circular dependencies
      const { CommentsService, ParentType } = await import('./comments.service');

      // Build the system-generated comment with clear status change indicator
      const actionLabels: Record<string, string> = {
        first_review: 'First Review Submitted',
        second_review_agree: 'Second Review - Agreed (Closed)',
        second_review_needs_info: 'Second Review - Needs More Info',
        add_info: 'Additional Information Added',
        claim: 'Issue Claimed',
        release: 'Claim Released',
      };

      const outcomeLabels: Record<string, string> = {
        no_concern: 'No Concern (Legitimate)',
        escalate: 'Escalate (SAR Required)',
      };

      const timestamp = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      // Build comment parts
      const parts = [
        `[STATUS CHANGE] ${actionLabels[action] || action}`,
      ];

      if (metadata?.issueType) {
        parts.push(`Issue Type: ${metadata.issueType}${metadata.issueTitle ? ` - ${metadata.issueTitle}` : ''}`);
      }

      if (metadata?.proposedOutcome) {
        parts.push(`Proposed Outcome: ${outcomeLabels[metadata.proposedOutcome] || metadata.proposedOutcome}`);
      }

      parts.push(`Action taken at: ${timestamp}`);

      if (reason && reason.trim()) {
        parts.push(''); // Empty line separator
        // Sanitize user-provided reason text to prevent XSS
        const sanitizedReason = reason.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;');
        parts.push(`Notes: ${sanitizedReason}`);
      }

      const commentText = parts.join('\n');

      const commentData = {
        parent_type: ParentType.AML_ISSUE,
        parent_id: issueHash,
        text: commentText,
        mention_uids: [],
      };

      await CommentsService.createComment(commentData);
    } catch (error) {
      // Log but don't throw - comment creation failure shouldn't block the main action
      console.error('[AMLService] Failed to create status change comment:', error);
      // Notify user that audit trail comment wasn't created
      toast.warning('Action completed successfully, but audit trail comment could not be created');
    }
  }

  // ============================================================================
  // ISSUES MANAGEMENT
  // ============================================================================

  /**
   * Get AML issues for an entity with filtering and pagination.
   * By default, fetches ALL pages to ensure complete data (backend limits to 100 per page).
   */
  async getIssues(
    entityId: string,
    params: AMLIssuesQueryParams = {}
  ): Promise<AMLIssuesResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }

      // FAST PATH: Single issue lookup for deep linking (notifications)
      if (params.issue_hash) {
        const queryParams = new URLSearchParams();
        queryParams.append('issue_hash', params.issue_hash);

        const url = `${this.baseUrl}/${entityId}/issues?${queryParams.toString()}`;
        const response = await api.get<{ issues: AMLIssue[]; total_count: number; page: number; page_size: number; has_more: boolean }>(url, {
          signal: params.signal
        });

        return {
          entity_id: entityId,
          issues: response.issues || [],
          total_count: response.total_count || 0,
          filtered_count: response.issues?.length || 0,
          pagination: {
            page: response.page || 1,
            page_size: response.page_size || 1,
            total_pages: 1,
            has_next: response.has_more || false,
            has_prev: false
          }
        };
      }

      // Use max page size (100) to minimize API calls
      const pageSize = 100;
      let allIssues: AMLIssue[] = [];
      let currentPage = 1;
      let totalCount = 0;
      let hasMorePages = true;

      while (hasMorePages) {
        // Check abort signal before each request
        if (params.signal?.aborted) {
          throw new Error('Request aborted');
        }

        const queryParams = new URLSearchParams();

        // Status filter
        if (params.status?.length) {
          params.status.forEach(s => queryParams.append('status', s));
        }

        // Issue type filter
        if (params.issue_type?.length) {
          params.issue_type.forEach(t => queryParams.append('issue_type', t));
        }

        // Claimed by filter
        if (params.claimed_by) {
          queryParams.append('claimed_by', params.claimed_by);
        }

        // Sorting
        if (params.sort_by) {
          queryParams.append('sort_by', params.sort_by);
        }
        if (params.sort_order) {
          queryParams.append('sort_order', params.sort_order);
        }

        // Date range (uses source_transaction_date)
        if (params.date_from) {
          queryParams.append('date_from', params.date_from);
        }
        if (params.date_to) {
          queryParams.append('date_to', params.date_to);
        }

        // Snooze visibility
        if (params.show_snoozed !== undefined) {
          queryParams.append('show_snoozed', String(params.show_snoozed));
        }

        // Pagination - always use max page size
        queryParams.append('page', String(currentPage));
        queryParams.append('page_size', String(pageSize));

        const url = `${this.baseUrl}/${entityId}/issues${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

        const response = await api.get<AMLIssuesResponse>(url, {
          signal: params.signal,
        });

        allIssues = [...allIssues, ...response.issues];
        totalCount = response.total_count;

        // Check if there are more pages
        const totalPages = Math.ceil(totalCount / pageSize);
        hasMorePages = currentPage < totalPages;
        currentPage++;
      }

      return {
        entity_id: entityId,
        issues: allIssues,
        total_count: totalCount,
        filtered_count: totalCount,
        pagination: {
          page: 1,
          page_size: allIssues.length,
          total_pages: 1,
          has_next: false,
          has_prev: false,
        },
      };
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to fetch AML issues');
    }
  }

  /**
   * Get a single AML issue by hash
   */
  async getIssue(
    entityId: string,
    issueHash: string,
    signal?: AbortSignal
  ): Promise<AMLIssue> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }

      const url = `${this.baseUrl}/${entityId}/issues/${issueHash}`;

      const response = await api.get<AMLIssue>(url, { signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to fetch AML issue');
    }
  }

  /**
   * Get AML summary for an entity (dashboard/summary page)
   */
  async getSummary(
    entityId: string,
    signal?: AbortSignal
  ): Promise<AMLSummaryResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }

      const url = `${this.baseUrl}/${entityId}/summary`;

      const response = await api.get<AMLSummaryResponse>(url, { signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to fetch AML summary');
    }
  }

  /**
   * Get AML counts for sidebar menu
   */
  async getCounts(
    entityId: string,
    options: {
      status?: AMLIssueStatus[];
      dateFrom?: string;
      dateTo?: string;
      signal?: AbortSignal;
    } = {}
  ): Promise<AMLCountsResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }

      const queryParams = new URLSearchParams();

      if (options.status?.length) {
        options.status.forEach(s => queryParams.append('status', s));
      }
      if (options.dateFrom) {
        queryParams.append('date_from', options.dateFrom);
      }
      if (options.dateTo) {
        queryParams.append('date_to', options.dateTo);
      }

      const url = `${this.baseUrl}/${entityId}/counts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await api.get<AMLCountsResponse>(url, {
        signal: options.signal,
      });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to fetch AML counts');
    }
  }

  /**
   * Get month-by-month counts for a specific AML issue type.
   * Efficient endpoint that returns only counts, not full issue data.
   * Used by AMLMonthTabs for fast rendering.
   */
  async getMonthCounts(
    entityId: string,
    issueType: AMLIssueType,
    options: {
      status?: AMLIssueStatus[];
      dateFrom?: string;
      dateTo?: string;
      signal?: AbortSignal;
    } = {}
  ): Promise<AMLMonthCountsResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }

      const queryParams = new URLSearchParams();
      queryParams.append('issue_type', issueType);

      if (options.status?.length) {
        options.status.forEach(s => queryParams.append('status', s));
      }
      if (options.dateFrom) {
        queryParams.append('date_from', options.dateFrom);
      }
      if (options.dateTo) {
        queryParams.append('date_to', options.dateTo);
      }

      const url = `${this.baseUrl}/${entityId}/month-counts?${queryParams.toString()}`;

      const response = await api.get<AMLMonthCountsResponse>(url, {
        signal: options.signal,
      });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to fetch AML month counts');
    }
  }

  /**
   * Get AML matrix summary aggregated by month and type
   * Used by AMLMonthMatrix to display issue counts without fetching all issues
   */
  async getMatrixSummary(
    entityId: string,
    options: {
      status?: AMLIssueStatus[];
      dateFrom?: string;
      dateTo?: string;
      signal?: AbortSignal;
    } = {}
  ): Promise<Record<string, Record<AMLIssueType, number>>> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }

      // Use the summary endpoint which returns aggregated counts
      const response = await this.getSummary(entityId, options.signal);

      // For now, we'll fetch all issues and aggregate on the client side
      // TODO: Add backend endpoint /aml/{entityId}/matrix-summary for server-side aggregation
      const issuesResponse = await this.getIssues(entityId, {
        status: options.status,
        date_from: options.dateFrom,
        date_to: options.dateTo,
        signal: options.signal,
      });

      // Aggregate issues by month and type
      const matrixData: Record<string, Record<AMLIssueType, number>> = {};

      issuesResponse.issues.forEach(issue => {
        const month = issue.source_transaction_date?.substring(0, 7) || 'unknown';

        if (!matrixData[month]) {
          matrixData[month] = {} as Record<AMLIssueType, number>;
        }

        if (!matrixData[month][issue.issue_type]) {
          matrixData[month][issue.issue_type] = 0;
        }

        matrixData[month][issue.issue_type]++;
      });

      return matrixData;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to fetch AML matrix summary');
    }
  }

  /**
   * Get user's claimed issues ("My Issues")
   */
  async getMyIssues(
    entityId: string,
    signal?: AbortSignal
  ): Promise<AMLIssuesResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }

      const url = `${this.baseUrl}/${entityId}/my-issues`;

      const response = await api.get<AMLIssuesResponse>(url, { signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to fetch my AML issues');
    }
  }

  // ============================================================================
  // WORKFLOW ACTIONS
  // ============================================================================

  /**
   * Claim an issue for first review (24-hour expiry)
   */
  async claimIssue(
    entityId: string,
    issueHash: string,
    signal?: AbortSignal
  ): Promise<ClaimIssueResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }

      const url = `${this.baseUrl}/${entityId}/claim`;
      const payload: ClaimIssueRequest = { issue_hash: issueHash };

      const response = await api.post<ClaimIssueResponse>(url, payload, { signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to claim issue');
    }
  }

  /**
   * Release a claim (allow others to claim)
   */
  async releaseClaim(
    entityId: string,
    issueHash: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }

      const url = `${this.baseUrl}/${entityId}/release-claim`;
      const payload: ReleaseClaimRequest = { issue_hash: issueHash };

      await api.post(url, payload, { signal });
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to release claim');
    }
  }

  /**
   * Submit first review (propose outcome)
   * Also creates a status change comment in the unified Comments system
   */
  async submitFirstReview(
    entityId: string,
    issueHash: string,
    proposedOutcome: ProposedOutcome,
    reason: string,
    version: number,
    signal?: AbortSignal,
    issueMetadata?: { issueType?: string; issueTitle?: string }
  ): Promise<FirstReviewResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }
      if (!reason || reason.trim().length < 3) {
        throw new Error('Reason must be at least 3 characters');
      }

      const url = `${this.baseUrl}/${entityId}/first-review`;
      const payload: FirstReviewRequest = {
        issue_hash: issueHash,
        proposed_outcome: proposedOutcome,
        reason: reason.trim(),
        version,
      };

      const response = await api.post<FirstReviewResponse>(url, payload, { signal });

      // Create status change comment for audit trail (fire-and-forget)
      this.createStatusChangeComment(issueHash, 'first_review', reason, {
        proposedOutcome,
        issueType: issueMetadata?.issueType,
        issueTitle: issueMetadata?.issueTitle,
      });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to submit first review');
    }
  }

  /**
   * Submit second review (approve or resubmit)
   * Also creates a status change comment in the unified Comments system
   */
  async submitSecondReview(
    entityId: string,
    issueHash: string,
    decision: SecondReviewDecision,
    reason: string,
    version: number,
    signal?: AbortSignal,
    issueMetadata?: { issueType?: string; issueTitle?: string; proposedOutcome?: ProposedOutcome }
  ): Promise<SecondReviewResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }
      if (decision === 'needs_info' && (!reason || reason.trim().length < 3)) {
        throw new Error('Reason is required for needs_info (minimum 3 characters)');
      }

      const url = `${this.baseUrl}/${entityId}/second-review`;
      const payload: SecondReviewRequest = {
        issue_hash: issueHash,
        decision,
        reason: reason.trim(),
        version,
      };

      const response = await api.post<SecondReviewResponse>(url, payload, { signal });

      // Create status change comment for audit trail (fire-and-forget)
      const action = decision === 'agree' ? 'second_review_agree' : 'second_review_needs_info';
      this.createStatusChangeComment(issueHash, action, reason || '', {
        proposedOutcome: issueMetadata?.proposedOutcome,
        issueType: issueMetadata?.issueType,
        issueTitle: issueMetadata?.issueTitle,
      });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to submit second review');
    }
  }

  /**
   * Add a note to an issue
   */
  async addNote(
    entityId: string,
    issueHash: string,
    content: string,
    signal?: AbortSignal
  ): Promise<AddNoteResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }
      if (!content || content.trim().length === 0) {
        throw new Error('Note content cannot be empty');
      }

      const url = `${this.baseUrl}/${entityId}/issues/${issueHash}/notes`;
      const payload: AddNoteRequest = { content: content.trim() };

      const response = await api.post<AddNoteResponse>(url, payload, { signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to add note');
    }
  }

  /**
   * Add information after NEEDS_INFO status (returns issue to SECOND_REVIEW)
   * Used when second reviewer requests more info and first reviewer provides it
   */
  async addInfo(
    entityId: string,
    issueHash: string,
    content: string,
    version: number,
    signal?: AbortSignal,
    issueMetadata?: { issueType?: string; issueTitle?: string }
  ): Promise<AddInfoResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }
      if (!content || content.trim().length < 3) {
        throw new Error('Information must be at least 3 characters');
      }

      const url = `${this.baseUrl}/${entityId}/add-info`;
      const payload: AddInfoRequest = {
        issue_hash: issueHash,
        content: content.trim(),
        version,
      };

      const response = await api.post<AddInfoResponse>(url, payload, { signal });

      // Create status change comment for audit trail (fire-and-forget)
      this.createStatusChangeComment(issueHash, 'add_info', content, {
        issueType: issueMetadata?.issueType,
        issueTitle: issueMetadata?.issueTitle,
      });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to add information');
    }
  }

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  /**
   * Bulk claim issues (max 50 per request)
   */
  async bulkClaim(
    entityId: string,
    issueHashes: string[],
    signal?: AbortSignal
  ): Promise<BulkClaimResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!issueHashes.length) {
        throw new Error('No issue hashes provided');
      }
      if (issueHashes.length > 50) {
        throw new Error('Maximum 50 issues per bulk claim request');
      }

      const url = `${this.baseUrl}/${entityId}/bulk-claim`;
      const payload: BulkClaimRequest = { issue_hashes: issueHashes };

      const response = await api.post<BulkClaimResponse>(url, payload, { signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to bulk claim issues');
    }
  }

  /**
   * Bulk first review (max 100 per request)
   */
  async bulkFirstReview(
    entityId: string,
    issueHashes: string[],
    proposedOutcome: ProposedOutcome,
    proposedReason: string,
    signal?: AbortSignal
  ): Promise<BulkFirstReviewResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!issueHashes.length) {
        throw new Error('No issue hashes provided');
      }
      if (issueHashes.length > 100) {
        throw new Error('Maximum 100 issues per bulk first review request');
      }
      if (!proposedReason || proposedReason.trim().length < 3) {
        throw new Error('Reason must be at least 3 characters');
      }

      const url = `${this.baseUrl}/${entityId}/bulk-first-review`;
      const payload: BulkFirstReviewRequest = {
        issue_hashes: issueHashes,
        proposed_outcome: proposedOutcome,
        proposed_reason: proposedReason.trim(),
      };

      const response = await api.post<BulkFirstReviewResponse>(url, payload, { signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to bulk submit first review');
    }
  }

  /**
   * Bulk second review (max 100 per request)
   */
  async bulkSecondReview(
    entityId: string,
    issueHashes: string[],
    decision: SecondReviewDecision,
    reason?: string,
    signal?: AbortSignal
  ): Promise<BulkSecondReviewResponse> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!issueHashes.length) {
        throw new Error('No issue hashes provided');
      }
      if (issueHashes.length > 100) {
        throw new Error('Maximum 100 issues per bulk second review request');
      }
      if (decision === 'needs_info' && (!reason || reason.trim().length < 3)) {
        throw new Error('Reason is required for needs_info (minimum 3 characters)');
      }

      const url = `${this.baseUrl}/${entityId}/bulk-second-review`;
      const payload: BulkSecondReviewRequest = {
        issue_hashes: issueHashes,
        decision,
        ...(reason && { reason: reason.trim() }),
      };

      const response = await api.post<BulkSecondReviewResponse>(url, payload, { signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to bulk submit second review');
    }
  }

  // ============================================================================
  // SNOOZE OPERATIONS
  // ============================================================================

  /**
   * Snooze a single issue (max 90 days)
   */
  async snoozeIssue(
    entityId: string,
    issueHash: string,
    snoozeUntil: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }

      // Validate snooze date (max 90 days)
      const snoozeDate = new Date(snoozeUntil);
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 90);

      if (snoozeDate > maxDate) {
        throw new Error('Snooze date cannot be more than 90 days in the future');
      }
      if (snoozeDate <= new Date()) {
        throw new Error('Snooze date must be in the future');
      }

      const url = `${this.baseUrl}/${entityId}/snooze`;
      const payload: SnoozeIssueRequest = {
        issue_hash: issueHash,
        snooze_until: snoozeUntil,
      };

      await api.post(url, payload, { signal });
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to snooze issue');
    }
  }

  /**
   * Bulk snooze issues (max 500 per request)
   */
  async bulkSnooze(
    entityId: string,
    issueHashes: string[],
    snoozeUntil: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!issueHashes.length) {
        throw new Error('No issue hashes provided');
      }
      if (issueHashes.length > 500) {
        throw new Error('Maximum 500 issues per bulk snooze request');
      }

      const url = `${this.baseUrl}/${entityId}/bulk-snooze`;
      const payload: BulkSnoozeRequest = {
        issue_hashes: issueHashes,
        snooze_until: snoozeUntil,
      };

      await api.post(url, payload, { signal });
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to bulk snooze issues');
    }
  }

  /**
   * Unsnooze a single issue
   */
  async unsnoozeIssue(
    entityId: string,
    issueHash: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }

      const url = `${this.baseUrl}/${entityId}/unsnooze`;
      const payload: UnsnoozeIssueRequest = { issue_hash: issueHash };

      await api.post(url, payload, { signal });
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to unsnooze issue');
    }
  }

  /**
   * Bulk unsnooze issues (max 500 per request)
   */
  async bulkUnsnooze(
    entityId: string,
    issueHashes: string[],
    signal?: AbortSignal
  ): Promise<void> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!issueHashes.length) {
        throw new Error('No issue hashes provided');
      }
      if (issueHashes.length > 500) {
        throw new Error('Maximum 500 issues per bulk unsnooze request');
      }

      const url = `${this.baseUrl}/${entityId}/bulk-unsnooze`;
      const payload: BulkUnsnoozeRequest = { issue_hashes: issueHashes };

      await api.post(url, payload, { signal });
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to bulk unsnooze issues');
    }
  }

  // ============================================================================
  // AML CHECK REFRESH
  // ============================================================================

  /**
   * Refresh AML checks for an entity
   *
   * Triggers on-demand AML detection checks matching compliance refresh pattern.
   *
   * @param entityId - Entity ID to refresh AML checks for
   * @param issueTypes - Optional array of AML rule types (K-V) to refresh. If not provided, runs all checks.
   * @param signal - Optional AbortSignal for cancellation
   * @returns Promise with execution results including timing and issue counts
   */
  async refreshAMLChecks(
    entityId: string,
    issueTypes?: string[],
    signal?: AbortSignal
  ): Promise<{
    success: boolean;
    entity_id: string;
    message: string;
    execution_time_seconds: number;
    checks_performed: string[];
    result: any;
  }> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }

      // Build query params if issue types are provided
      const params = issueTypes && issueTypes.length > 0
        ? { issue_types: issueTypes.join(',') }
        : {};

      const url = `${this.baseUrl}/${entityId}/refresh`;
      const response = await api.post<{
        success: boolean;
        entity_id: string;
        message: string;
        execution_time_seconds: number;
        checks_performed: string[];
        result: any;
      }>(url, {}, { params, signal });

      return response;
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to refresh AML checks');
    }
  }

  // ============================================================================
  // ADMIN OPERATIONS
  // ============================================================================

  /**
   * Reassign an issue to a different reviewer (firm_admin only)
   */
  async reassignIssue(
    entityId: string,
    issueHash: string,
    newReviewerId: string,
    reason: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }

      const url = `${this.baseUrl}/${entityId}/reassign`;
      const payload: ReassignIssueRequest = {
        issue_hash: issueHash,
        new_reviewer_id: newReviewerId,
        reason: reason.trim(),
      };

      await api.post(url, payload, { signal });
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to reassign issue');
    }
  }

  /**
   * Override separation of duties (firm_admin only)
   */
  async overrideSeparation(
    entityId: string,
    issueHash: string,
    reason: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      if (!isValidEntityId(entityId)) {
        throw new Error('Invalid entity ID provided');
      }
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format');
      }

      const url = `${this.baseUrl}/${entityId}/override-separation`;
      const payload: OverrideSeparationRequest = {
        issue_hash: issueHash,
        reason: reason.trim(),
      };

      await api.post(url, payload, { signal });
    } catch (error: unknown) {
      if (isAMLRequestCancelled(error)) {
        throw error;
      }

      throw this.handleError(error, 'Failed to override separation of duties');
    }
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Get issue type display name
   */
  getIssueTypeName(type: AMLIssueType): string {
    return AML_ISSUE_TYPE_INFO[type]?.name || type;
  }

  /**
   * Get issue type description
   */
  getIssueTypeDescription(type: AMLIssueType): string {
    return AML_ISSUE_TYPE_INFO[type]?.description || 'AML issue';
  }

  /**
   * Get status badge configuration
   */
  getStatusBadgeConfig(status: AMLIssueStatus) {
    return AML_STATUS_BADGE_CONFIG[status] || {
      color: 'gray',
      text: status,
      bgClass: 'bg-gray-100 text-gray-800',
    };
  }

  /**
   * Check if an issue is currently snoozed
   */
  isIssueSnoozed(issue: AMLIssue): boolean {
    if (!issue.snoozed_until) return false;
    return new Date(issue.snoozed_until) > new Date();
  }

  /**
   * Check if user can perform second review (separation of duties)
   */
  canPerformSecondReview(issue: AMLIssue, userId: string): boolean {
    return (
      issue.status === 'second_review' &&
      issue.first_reviewer_id !== userId
    );
  }

  /**
   * Check if user can add info (first reviewer when issue is in NEEDS_INFO status)
   */
  canAddInfo(issue: AMLIssue, userId: string): boolean {
    return (
      issue.status === 'needs_info' &&
      issue.first_reviewer_id === userId
    );
  }

  /**
   * Check if claim is expired
   */
  isClaimExpired(issue: AMLIssue): boolean {
    if (!issue.claim_expires_at) return false;
    return new Date(issue.claim_expires_at) <= new Date();
  }

  /**
   * Format risk score for display
   */
  formatRiskScore(score: number): { label: string; color: string } {
    if (score >= 80) return { label: 'Critical', color: 'text-red-700' };
    if (score >= 60) return { label: 'High', color: 'text-orange-700' };
    if (score >= 40) return { label: 'Medium', color: 'text-yellow-700' };
    return { label: 'Low', color: 'text-green-700' };
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  /**
   * Standardized error handling
   */
  private handleError(error: unknown, defaultMessage: string): Error {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: { detail?: string };
      };
    };

    if (axiosError.response?.status === 404) {
      return new Error('Issue not found or not accessible');
    }
    if (axiosError.response?.status === 403) {
      return new Error('Access denied. Please check your permissions.');
    }
    if (axiosError.response?.status === 409) {
      return new Error('Conflict: Issue was modified by another user. Please refresh and try again.');
    }
    if (axiosError.response?.status === 400) {
      return new Error(axiosError.response.data?.detail || 'Invalid request');
    }

    const message = axiosError.response?.data?.detail || defaultMessage;
    return new Error(message);
  }
}

// Export singleton instance
export const amlService = new AMLService();

// Export class for testing
export { AMLService };
